import { type ComponentProps, type ReactNode, createContext, use, useRef, useState } from 'react';
import { mergeProps } from 'react-aria';
import {
  type Key,
  type SelectionMode,
  Autocomplete,
  Collection,
  ListBoxLoadMoreItem,
  useFilter,
} from 'react-aria-components';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { useDeferredState } from '@gonfalon/react/useDeferredState';
import { InfiniteSuspenseQuery } from '@gonfalon/suspense/InfiniteSuspenseQuery';
import { SuspenseQuery } from '@gonfalon/suspense/SuspenseQuery';
import { isDefined } from '@gonfalon/types/isDefined';
import {
  type ListBoxItemProps,
  type ListBoxProps,
  Button,
  Dialog,
  DialogTrigger,
  IconButton,
  ListBox,
  ListBoxContext,
  ListBoxItem,
  Popover,
  ProgressBar,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseQueryOptions,
  UseSuspenseInfiniteQueryOptions,
  UseSuspenseQueryOptions,
} from '@tanstack/react-query';

import { SearchBox } from '../search-box';

import styles from './List.module.css';

type BaseProps<T extends { id: string } = { id: string }> = {
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  onAction?: (key: string) => void;
  footer?: ReactNode;
} & Omit<ListBoxProps<T>, 'items' | 'children' | 'onAction' | 'selectedKeys' | 'onSelectionChange'>;

function ListComponent(props: { items?: never; children: ReactNode | ReactNode[] } & BaseProps): ReactNode;
function ListComponent<Item extends { id: string }>(
  props: { items: Item[]; children: (item: Item) => ReactNode } & BaseProps<Item>,
): ReactNode;
function ListComponent<TQueryFnData, TError, TData, TQueryKey extends QueryKey, Item extends { id: string }>(
  props: {
    queryErrorSeverity?: ComponentProps<typeof SuspenseQuery>['errorSeverity'];
    queryOwner: ComponentProps<typeof SuspenseQuery>['owner'];
    queryErrorIgnoredStatuses?: ComponentProps<typeof SuspenseQuery>['errorIgnoredStatuses'];
    query:
      | UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
      | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
      | ((search: string) => UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>)
      | ((search: string) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>);
    toItems: (data: TData, search: string) => Item[];
    children: (item: Item) => ReactNode;
  } & BaseProps<Item>,
): ReactNode;
function ListComponent<
  TQueryFnData,
  TError,
  TPageParam,
  TInfiniteData extends InfiniteData<TQueryFnData, TPageParam>,
  TQueryKey extends QueryKey,
  Item extends { id: string },
>(
  props: {
    queryErrorSeverity?: ComponentProps<typeof SuspenseQuery>['errorSeverity'];
    queryOwner: ComponentProps<typeof SuspenseQuery>['owner'];
    queryErrorIgnoredStatuses?: ComponentProps<typeof SuspenseQuery>['errorIgnoredStatuses'];
    infiniteQuery:
      | UseSuspenseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>
      | UseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>
      | ((
          search: string,
        ) => UseSuspenseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>)
      | ((
          search: string,
        ) => UseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>);
    toItems: (data: TInfiniteData, search: string) => Item[];
    children: (item: Item) => ReactNode;
  } & BaseProps<Item>,
): ReactNode;

function ListComponent<
  TQueryFnData,
  TError,
  TData extends TQueryFnData,
  TQueryKey extends QueryKey,
  TPageParam,
  TInfiniteData extends InfiniteData<TQueryFnData, TPageParam>,
  Item extends { id: string },
>({
  items,
  query,
  infiniteQuery,
  toItems,
  children,
  onAction,
  renderEmptyState = () => <ListItemPlaceholder>No results</ListItemPlaceholder>,
  selectedKeys,
  onSelectionChange,
  footer,
  ...props
}: (
  | // rendering static list items as children
    {
      items?: never;
      query?: never;
      infiniteQuery?: never;
      toItems?: never;
      queryErrorSeverity?: never;
      queryOwner?: never;
      queryErrorIgnoredStatuses?: never;
      children: ReactNode | ReactNode[];
    }
  // rendering dynamic, synchronous list items
  | {
      items: Item[];
      query?: never;
      infiniteQuery?: never;
      toItems?: never;
      queryErrorSeverity?: never;
      queryOwner?: never;
      queryErrorIgnoredStatuses?: never;
      children: (item: Item) => ReactNode;
    }
  // rendering async list items
  | {
      items?: never;
      query:
        | UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
        | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
        | ((search: string) => UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>)
        | ((search: string) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>);
      infiniteQuery?: never;
      toItems: (data: TData, search: string) => Item[];
      queryErrorSeverity?: ComponentProps<typeof SuspenseQuery>['errorSeverity'];
      queryOwner: ComponentProps<typeof SuspenseQuery>['owner'];
      queryErrorIgnoredStatuses?: ComponentProps<typeof SuspenseQuery>['errorIgnoredStatuses'];
      children: (item: Item) => ReactNode;
    }
  // rendering async, infinite list items
  | {
      items?: never;
      query?: never;
      infiniteQuery:
        | UseSuspenseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>
        | UseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>
        | ((
            search: string,
          ) => UseSuspenseInfiniteQueryOptions<
            TQueryFnData,
            TError,
            TInfiniteData,
            TQueryFnData,
            TQueryKey,
            TPageParam
          >)
        | ((
            search: string,
          ) => UseInfiniteQueryOptions<TQueryFnData, TError, TInfiniteData, TQueryFnData, TQueryKey, TPageParam>);
      toItems: (data: TInfiniteData, search: string) => Item[];
      queryErrorSeverity?: ComponentProps<typeof SuspenseQuery>['errorSeverity'];
      queryOwner: ComponentProps<typeof SuspenseQuery>['owner'];
      queryErrorIgnoredStatuses?: ComponentProps<typeof SuspenseQuery>['errorIgnoredStatuses'];
      children: (item: Item) => ReactNode;
    }
) &
  BaseProps<Item>) {
  const handleAction = onAction
    ? (key: Key) => {
        onAction?.(String(key));
      }
    : undefined;

  function handleSelectionChange(keys: 'all' | Iterable<Key>) {
    if (keys === 'all') {
      return;
    }

    onSelectionChange?.(Array.from(keys).map(String));
  }

  const loadingFallback = (
    <ListItemPlaceholder>
      <ProgressBar isIndeterminate aria-label="Loading" className={styles.progressBar} />
    </ListItemPlaceholder>
  );

  function renderList(listItems?: Item[], options: { loadMore?: () => void; isLoading?: boolean } = {}) {
    return (
      <>
        <ListBox
          {...mergeProps({ className: styles.List }, props)}
          items={listItems}
          onAction={handleAction}
          renderEmptyState={renderEmptyState}
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          shouldFocusWrap
        >
          {options.loadMore ? (
            <>
              <Collection items={listItems}>{children}</Collection>
              <ListBoxLoadMoreItem scrollOffset={0.1} onLoadMore={options.loadMore} isLoading={options.isLoading}>
                {loadingFallback}
              </ListBoxLoadMoreItem>
            </>
          ) : (
            children
          )}
        </ListBox>
        {footer && (
          <ListFooterContext value={{ selectionMode: props.selectionMode, selectedKeys, onSelectionChange }}>
            {footer}
          </ListFooterContext>
        )}
      </>
    );
  }

  // if "items" array is provided, render the list with the items
  if (items) {
    return renderList(items);
  }

  // if "query" and "toItems" functions are provided, render the list with the items from the query
  if (query && toItems) {
    const { queryErrorSeverity = 'high', queryOwner, queryErrorIgnoredStatuses } = props;
    const { search } = use(SearchContext);

    const queryOptions = typeof query === 'function' ? query(search) : query;

    return (
      <SuspenseQuery<TQueryFnData, TError, TData, TQueryKey>
        query={queryOptions}
        fallback={loadingFallback}
        boundary={({ error, resetErrorBoundary }) => (
          <ListErrorBoundary
            error={error instanceof Error ? error.message : String(error)}
            reset={resetErrorBoundary}
          />
        )}
        errorSeverity={queryErrorSeverity}
        owner={queryOwner}
        errorIgnoredStatuses={queryErrorIgnoredStatuses}
      >
        {({ data }) => renderList(toItems(data, search))}
      </SuspenseQuery>
    );
  }

  // if "infiniteQuery" and "toItems" functions are provided, render the list with the items from the infinite query
  if (infiniteQuery && toItems) {
    const { queryErrorSeverity = 'high', queryOwner, queryErrorIgnoredStatuses } = props;
    const { search, onFetchStart, onFetchEnd } = use(SearchContext);

    const infiniteQueryOptions = typeof infiniteQuery === 'function' ? infiniteQuery(search) : infiniteQuery;

    return (
      <InfiniteSuspenseQuery
        infiniteQuery={infiniteQueryOptions}
        onFetchStart={onFetchStart}
        onFetchEnd={onFetchEnd}
        fallback={loadingFallback}
        boundary={({ error, resetErrorBoundary }) => (
          <ListErrorBoundary
            error={error instanceof Error ? error.message : String(error)}
            reset={resetErrorBoundary}
          />
        )}
        errorSeverity={queryErrorSeverity}
        owner={queryOwner}
        errorIgnoredStatuses={queryErrorIgnoredStatuses}
      >
        {({ data, hasNextPage, isFetchingNextPage, fetchNextPage }) =>
          renderList(toItems(data, search), {
            loadMore: () => {
              if (hasNextPage && !isFetchingNextPage) {
                void fetchNextPage();
              }
            },
            isLoading: isFetchingNextPage,
          })
        }
      </InfiniteSuspenseQuery>
    );
  }

  if (typeof children !== 'function') {
    // if "children" is not a function, render the list with the children as is
    return renderList();
  }

  // or else, throw an error because the props are invalid
  throw new Error(`Invalid List component props.`);
}

const SearchContext = createContext<{
  search: string;
  setState: (value: string) => void;
  isPending: boolean;
  onFetchStart: () => void;
  onFetchEnd: () => void;
}>({
  search: '',
  setState: () => {},
  isPending: false,
  onFetchStart: () => {},
  onFetchEnd: () => {},
});

function ListSearch({
  children,
  focusOnMount = false,
  async,
  filterOptions,
  placeholder,
}: {
  children: ReactNode;
  focusOnMount?: boolean;
  placeholder?: string;
} & ({ async: true; filterOptions?: never } | { async?: never; filterOptions?: Intl.CollatorOptions })) {
  const [state, setState, isDeferredStatePending] = useDeferredState('');
  const { contains } = useFilter({ sensitivity: 'base', usage: 'search', numeric: true, ...filterOptions });
  const [isPending, setIsPending] = useState(false);

  return (
    <Autocomplete inputValue={state.current} onInputChange={setState} filter={async ? undefined : contains}>
      <SearchContext
        value={{
          search: state.deferred,
          setState,
          isPending: isDeferredStatePending || isPending,
          onFetchStart: () => setIsPending(true),
          onFetchEnd: () => setIsPending(false),
        }}
      >
        <ListBoxContext value={{ escapeKeyBehavior: 'none' }}>
          <SearchBox
            isPending={isDeferredStatePending}
            focusOnMount={focusOnMount}
            className={styles.SearchBox}
            placeholder={placeholder}
          />
          {children}
        </ListBoxContext>
      </SearchContext>
    </Autocomplete>
  );
}

type ListItemProps<T> = ListBoxItemProps<T>;

function ListItem<T extends object>({
  label,
  marker,
  textValue,
  description,
  ...props
}: { marker?: ReactNode; label: ReactNode; description?: ReactNode; textValue: string } & Omit<
  ListItemProps<T>,
  'children' | 'textValue'
>) {
  return (
    <ListBoxItem<T> {...mergeProps({ className: styles.ListItem }, props)} textValue={textValue}>
      <Text slot="label">
        {marker && <div className={styles.markerContainer}>{marker}</div>}
        {label}
      </Text>
      {description && <Text slot="description">{description}</Text>}
    </ListBoxItem>
  );
}

function ListItemPlaceholder({ children }: { children: ReactNode }) {
  return <div className={styles.ListItemPlaceholder}>{children}</div>;
}

const ListFooterContext = createContext<{
  selectionMode?: SelectionMode;
  selectedKeys?: string[];
  onSelectionChange?: (selection: string[]) => void;
}>({});

function ListFooter({
  renderSelectedItem,
  onClear,
  children,
}: {
  renderSelectedItem?: (key: string) => ReactNode;
  onClear?: () => void;
  children?: ReactNode | ReactNode[];
}) {
  const footerContext = use(ListFooterContext);
  const selected = footerContext.selectedKeys ?? [];
  const numSelectedKeys = selected.length;

  const childrenArray = [
    renderSelectedItem && footerContext.selectionMode === 'single' && numSelectedKeys === 1 ? (
      <div key="single-selection-display" className={styles.singleSelectionDisplay}>
        <Icon name="check-circle" size="tiny" className={styles.checkCircleIcon} />
        <TextTruncator>{renderSelectedItem(selected[0])}</TextTruncator>
      </div>
    ) : null,
    renderSelectedItem &&
    footerContext.selectionMode === 'multiple' &&
    numSelectedKeys > 0 &&
    footerContext.onSelectionChange ? (
      <DialogTrigger key="multiple-selection-button">
        <Button size="small" variant="minimal" className={styles.multipleSelectionButton}>
          {numSelectedKeys} selected
          <Icon name="chevron-down" size="tiny" className={styles.chevronDownIcon} />
        </Button>
        <Popover placement="bottom start" className={styles.selectedItemsPopover}>
          <Dialog>
            <SelectedItemsList selected={selected} onSelectionChange={footerContext.onSelectionChange}>
              {renderSelectedItem}
            </SelectedItemsList>
          </Dialog>
        </Popover>
      </DialogTrigger>
    ) : null,
    ...(Array.isArray(children) ? children : [children]),
    onClear && selected.length > 0 ? (
      <Button key="clear-button" size="small" variant="minimal" className={styles.clearButton} onPress={onClear}>
        Clear
      </Button>
    ) : null,
  ].filter(isDefined);

  if (childrenArray.length > 0) {
    return <div className={styles.ListFooter}>{childrenArray}</div>;
  }

  return null;
}

function SelectedItemsList({
  selected,
  onSelectionChange,
  children,
}: {
  selected: string[];
  onSelectionChange: (selection: string[]) => void;
  children: (key: string) => ReactNode;
}) {
  const initialSelected = useRef(selected);

  const list = (
    <List
      selectionMode="multiple"
      selectedKeys={selected}
      onSelectionChange={(selection) => {
        onSelectionChange(Array.from(selection));
      }}
      items={initialSelected.current.map((s) => ({ id: s }))}
      autoFocus
    >
      {(item) => children(item.id)}
    </List>
  );

  // render the selected items list as searchable when there are more than 10 items
  if (initialSelected.current.length > 10) {
    return <List.Search focusOnMount>{list}</List.Search>;
  }

  return list;
}

function ListErrorBoundary({ error, reset }: { error: string; reset: () => void }) {
  return (
    <div className={styles.errorBoundary}>
      <Icon className={styles.errorIcon} name="alert-rhombus" size="small" />
      <div className={styles.contentWrapper}>
        <div className={styles.errorTitleContainer}>
          <div className={styles.errorTitle}>Error loading data</div>
          <TooltipTrigger>
            <IconButton
              icon="sync"
              aria-label="Retry loading data"
              size="small"
              variant="minimal"
              onPress={reset}
              className={styles.retryButton}
            />
            <Tooltip>Retry</Tooltip>
          </TooltipTrigger>
        </div>
        <div className={styles.errorDescription}>{error}</div>
      </div>
    </div>
  );
}

export const List = Object.assign(ListComponent, {
  Search: ListSearch,
  Item: Object.assign(ListItem, {
    Placeholder: ListItemPlaceholder,
  }),
  Footer: ListFooter,
});
